import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import { isInputValid, isTestCodeValid, MAX_LENGTH, setTestCodeManually, TestCodeButtonProps, TestLaunchResult, TEST_CODE_INIT, validateTestCodeAsync } from './models';
import './TestCodeButton.scss';
import { TEST_TAKER_TYPE } from './models/TestCodeButton.types';
import Spinner from '../Spinner/Spinner';
import ga4 from '../../shared/analyticsEvents';

function TestCodeButton({onButtonClick, testCodeFromLink, reset}: TestCodeButtonProps): JSX.Element {

  const inputEl = useRef<HTMLInputElement>(null);
  const buttonEl = useRef<HTMLButtonElement>(null);

  // tracks user keystrokes in array
  const [userInput, setUserInput] = useState([] as string[]);
  // tracks input Elements value
  const [testCode, setTestCode] = useState(TEST_CODE_INIT);
  // tracks cursor pos
  const [cursor, setCursor] = useState(0);
  // manual switch to fire cursor effect
  const [toggle, setToggle] = useState(false);
  // tracks button state
  const [isDisabled, setIsDisabled] = useState(true);
  // tracks if input is fully selected
  const [isSelected, setIsSelected] = useState(false);
  // tracks if error message from validateTestTicket
  const [errMsg, setErrMsg] = useState<string>('');
  // tracks spinner
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const resetState = useCallback(() => {
    setUserInput([]);
    setTestCode(TEST_CODE_INIT);
    setCursor(0);
    setIsDisabled(true);
    setIsSelected(false);
    setErrMsg('');
    setIsLoading(false);
  }, []);

  /**
   * calls validateTestCodeAsync, awaits response, calls launchTestAsync, sets UI accordingly
   * @param testCode
   * @returns if successful - TestLaunchResult; else - null
   */
  const asyncTestLaunchHandler = useCallback(async(testCode: string, testTakerType: TEST_TAKER_TYPE=TEST_TAKER_TYPE.FROM_EMAIL):Promise<TestLaunchResult|null> => {
    setIsLoading(true);
    setErrMsg('');
    const response = await validateTestCodeAsync(testCode, testTakerType);
    if (!response.errMsg) {
      try {
        new URL(response.startTestURL); // this will throw an error if the URL is invalid
        window.location.href = response.startTestURL;
      } catch (_) {
        console.error('Invalid URL received from API: ', response.startTestURL);
        setErrMsg('Sorry, the test server is unavailable at this time. Please try again later.');
      }
      // const testLaunchResponse = await launchTestAsync(response.testTakerResultID);
      // if (!testLaunchResponse.errMsg) {
      //   setIsLoading(false);
      //   return testLaunchResponse;
      // } else {
      //   setErrMsg(testLaunchResponse.errMsg);
      // }
    } else {
      setErrMsg(response.errMsg);
    }
    setIsLoading(false);
    return null;
  }, []);

  /**
 * parse userInput state and set TestCode state accordingly
 * @param {string[]} userInputArr : [...userInput]
 */
  const parseInput = useCallback((userInputArr: string[]): void => {
    setUserInput(userInputArr);
    if (userInputArr.length <= 3 ) {
      setTestCode(userInputArr.join('').padEnd(MAX_LENGTH, TEST_CODE_INIT.substring(userInputArr.length)));
    } else {
      let strBuilder = '';
      userInputArr.forEach( (input, index) => {
        strBuilder += input;
        if (index === 2) {
          strBuilder += '-';
        }
      });
      setTestCode(strBuilder.padEnd(MAX_LENGTH, TEST_CODE_INIT.substring(strBuilder.length)));
    }
  }, []);

  /**
   *
   * @returns true only if user input has all 6 characters registered
   */
  const isInputFull = useCallback((uInput?: string[]):boolean => {
    if (uInput) {
      return uInput.length === 6;
    } else {
      return userInput.length === 6;
    }
  }, [userInput]);

  const onClickTestCodeLaunch = useCallback(async() => {
    if (!testCode) {
      return;
    } else {
      const success = await asyncTestLaunchHandler(testCode);
      if (success) {
        onButtonClick(success);
        ga4.onGA4LaunchedTicket();
      }
    }
  }, [asyncTestLaunchHandler, onButtonClick, testCode]);

  /**
   * Input Element's input-change event-handler - parses user input and updates cursor
   * @param event
   */
  const onInputChange = useCallback((event: React.FormEvent<HTMLInputElement>): void => {
    if ( event && event.nativeEvent ) {
      const e = event.nativeEvent as InputEvent;
      let uInput = [...userInput];
      const lessThan3 = uInput.length <= 3;

      const selectionStart = event.currentTarget.selectionStart ?
        event.currentTarget.selectionStart :
        0;

      //edge-case: if fully selected, delete all input and restart
      if (isSelected && isInputValid(e.data)) {
        uInput = [];
        setIsSelected(false);
      }

      // Backspace or Delete keystroke conditions
      if (e.inputType === 'deleteContentBackward' || e.inputType === 'deleteContentForward') {
        if (!uInput.length){
          setCursor(0);
          return;
        }
        else {
          const filtered = uInput.filter( input => event.currentTarget.value.includes(input) );
          if (filtered.length === uInput.length) {
            // backspace or delete removed hyphen
            if (selectionStart === 3) {
              if (e.inputType === 'deleteContentBackward') {
                uInput.splice(selectionStart - 1, 1);
                parseInput(uInput);
                setCursor(selectionStart - 1);
              } else if (e.inputType === 'deleteContentForward') {
                uInput.splice(selectionStart, 1);
                parseInput(uInput);
                setCursor(selectionStart);
              }
            } else if ( lessThan3 && selectionStart >= uInput.length) {
              // backspace or delete from cursor end position
              uInput.pop();
              parseInput(uInput);
              setCursor(uInput.length);
            } else if ( !lessThan3 && selectionStart >= uInput.length + 1){
              // backspace or delete from cursor end position
              uInput.pop();
              parseInput(uInput);
              setCursor(uInput.length + 1);
            } else {
              // duplicate values, so splice at current position
              if (e.inputType === 'deleteContentBackward') {
                lessThan3 ?
                  uInput.splice(selectionStart, 1):
                  uInput.splice(selectionStart - 1, 1);
                parseInput(uInput);

                setCursor(selectionStart);
              } else if (e.inputType === 'deleteContentForward') {
                uInput.splice(selectionStart, 1);
                parseInput(uInput);
                setCursor(selectionStart);
              }

              // edge-case: user presses delete at end pos, no characters are deleted
              const isFull = isInputFull(uInput);
              if (isFull) {
                isDisabled && setIsDisabled(false);
                buttonEl.current?.focus();
              } else {
                !isDisabled && setIsDisabled(true);
              }
              isSelected && setIsSelected(false);
              return;
            }
          } else {
            // An element from userInput was deleted
            // parse, set cursor, disable button
            parseInput(filtered);
            setCursor(selectionStart);
            !isDisabled && setIsDisabled(true);
            isSelected && setIsSelected(false);
            return;
          }
        }
      } else if ( e.inputType === 'insertText') {
        // Any other keystroke other than backspace or delete

        // If input is already full, focus on button
        let isFull = isInputFull(uInput);
        if (isFull) {
          if (isDisabled) {
            setIsDisabled(false);
          }
          buttonEl.current?.focus();
          return;
        } else {
          if (!isDisabled) {
            setIsDisabled(true);
          }
        }

        // if invalid return cursor to current pos
        const isValid = isInputValid(e.data);
        if (!isValid) {
          // offset by one to account for extra char just entered
          setCursor(selectionStart - 1);

          // if cursor is the same value, useEffect won't fire
          setToggle(!toggle);
          return;
        }

        // Checks passed, register input
        if (!!e.data && typeof e.data !== 'string') {
          !lessThan3 && selectionStart > 3 && selectionStart < 7 ?
            uInput.splice(selectionStart - 2, 0, e.data):
            uInput.splice(selectionStart - 1, 0, e.data);
        } else {
          !lessThan3 && selectionStart > 3 && selectionStart < 7 ?
            uInput.splice(selectionStart - 2, 0, e.data?.toUpperCase() as string):
            uInput.splice(selectionStart - 1, 0, e.data?.toUpperCase() as string);
        }

        parseInput(uInput);

        // set cursor based on size with new userInput added
        selectionStart !== 3 ?
          setCursor(selectionStart):
          setCursor(selectionStart + 1);

        // if now full, jump to button, else disable, if not already
        isFull = isInputFull(uInput);
        if (isFull) {
          setIsDisabled(false);
          buttonEl.current?.focus();
        } else {
          !isDisabled && setIsDisabled(true);
        }
        return;
      }
    }
  }, [isDisabled, isInputFull, isSelected, parseInput, toggle, userInput]);

  /**
   * OnPasteHandler - updates testCode field only if valid: XXX-XXX
   */
  const onPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    const input = event.clipboardData.getData('text');
    let text = input;

    // if Ctrl+C from Outlook, there are hidden white spaces to trim
    text = text.replace(/[\n\t\r ]/g,'');

    if (isTestCodeValid(text)) {
      setTestCodeManually(text, setUserInput, setTestCode, setCursor, setIsDisabled);
      setTimeout(() => buttonEl.current?.focus(), 0);
    } else {
      // edge case: Ctrl+C from Outlook preview prepends link
      const splitText = input.split(' ');
      if (splitText.length > 2) {
        text = splitText[1].replace(/[\n\t\r ]/g, '');
        if (isTestCodeValid(text)) {
          setTestCodeManually(text, setUserInput, setTestCode, setCursor, setIsDisabled);
          setTimeout(() => buttonEl.current?.focus(), 0);
        }
      }
    }
  }, []);

  /**
   * Input Elements onSelect Event Handler.
   * Tracks if selection is fully highlighted.
   * Fires contingent to input-change.
   * Sets and unsets isSelected state
   * @param event
   */
  const onSelectionChange = useCallback((event: React.FormEvent<HTMLInputElement>): void => {
    if (event.currentTarget) {
      const start = event.currentTarget.selectionStart; // 0 at start
      const end = event.currentTarget.selectionEnd; // 7 at end
      start !== null && end !==null && end - start !== 7 ?
        isSelected && setIsSelected(false) :
        !isSelected && setIsSelected(true);
    }
  }, [isSelected]);

  // Update testCode with testCodeFromLink
  useEffect(() => {
    if (testCodeFromLink && testCodeFromLink.length === 7) {
      if (isTestCodeValid(testCodeFromLink)) {
        setTestCodeManually(testCodeFromLink, setUserInput, setTestCode, setCursor, setIsDisabled);
        setTimeout(() => buttonEl.current?.focus(), 0);
        // asyncValidateHandler(testCodeFromLink); // validate on redirect
      }
    }
  }, [asyncTestLaunchHandler, testCodeFromLink]);

  // Update cursor after comp is re-rendered
  useEffect(() => {
    const input = inputEl.current;
    if (input) input.setSelectionRange(cursor, cursor);
  }, [inputEl, testCode, cursor, toggle]);

  // Update disabled button state after comp is re-rendered
  useEffect(() => {
    const button = buttonEl.current;
    if (button && !isDisabled) {
      button.focus();
    }
  }, [buttonEl, isDisabled]);

  // run resetState callback, when reset prop is toggled by parent
  useEffect(() => {
    if (reset) {
      resetState();
    }
  }, [reset, resetState]);

  return (
    <>
      {
        !!errMsg &&
        <div className="test-code-btn error-message-container">
          <span className="error-message">
            {
              errMsg
            }
          </span>
        </div>
      }
      <div className='nqt-homepage--test-code'>
        {
          isLoading && <Spinner/>
        }
        <input id='nqt-homepage--test-code-input'
          ref={inputEl}
          onInput={onInputChange}
          onPaste={onPaste}
          onSelect={onSelectionChange}
          value={testCode}>
        </input>
        <button className='nqt-homepage--test-code-btn'
          ref={buttonEl}
          type='button'
          disabled={isDisabled}
          onClick={() => onClickTestCodeLaunch()}>
          <FontAwesomeIcon className='fa-arrow-right' icon={faArrowRight} />
          <span className="d-none">Submit test code</span>
        </button>
      </div>
    </>
  );
}

export default TestCodeButton;